import { difference, intersection } from "es-toolkit";

import type { SlotId } from "../../schemas/index.ts";
import type { GetRuntimeManifest } from "../../types/index.ts";
import type { NodeDesignsState } from "../types.ts";
import type { GetDesignerNodeDataById } from "./getDesignerNodeData.ts";
import { getDesignerNodeDataByIdCreate } from "./getDesignerNodeData.ts";
import { getNodeSlotIdCreate } from "./getNodeSlotId.ts";
import type { ResolveActiveNodesData } from "./resolveActiveNodesData.ts";
import { resolveActiveNodesDataCreate } from "./resolveActiveNodesData.ts";
import type { ResolveNodeAssociations } from "./resolveNodeAssociations.ts";
import { resolveNodeAssociationsSelector } from "./resolveNodeAssociations.ts";
import type { ResolveNodeIdsWithConfigReferences } from "./resolveNodeIdsWithConfigReferences.ts";
import { resolveNodeIdsWithConfigReferencesCreate } from "./resolveNodeIdsWithConfigReferences.ts";

interface Options {
  getNode: GetDesignerNodeDataById;
  resolveActiveNodes: ResolveActiveNodesData;
  resolveNodeAssociations: ResolveNodeAssociations;
  resolveNodeIdsWithConfigReferences: ResolveNodeIdsWithConfigReferences;
}

export const resolveNodeLinkagesPreventingMoveToSlotCreateFromState = (
  state: NodeDesignsState,
  getRuntimeManifest: GetRuntimeManifest,
) => {
  const getNode = getDesignerNodeDataByIdCreate(state);
  const getSlotId = getNodeSlotIdCreate(state);
  const resolveActiveNodes = resolveActiveNodesDataCreate(getRuntimeManifest, getSlotId, getNode);
  const resolveNodeAssociations = resolveNodeAssociationsSelector({ nodeDesigns: state });
  const resolveNodeIdsWithConfigReferences = resolveNodeIdsWithConfigReferencesCreate(state.designsById);

  return resolveNodeLinkagesPreventingMoveToSlotCreate({
    getNode,
    resolveActiveNodes,
    resolveNodeAssociations,
    resolveNodeIdsWithConfigReferences,
  });
};

export const resolveNodeLinkagesPreventingMoveToSlotCreate =
  ({ resolveActiveNodes, resolveNodeAssociations, resolveNodeIdsWithConfigReferences }: Options) =>
  (nodeId: number, targetSlot: SlotId) => {
    const activeNodeIds = resolveActiveNodes(nodeId).map((n) => n.id);
    const targetSlotActiveNodeIds = resolveActiveNodes(targetSlot.parentId).map((n) => n.id);
    const missingIfMovedActiveNodeIds = difference(activeNodeIds, targetSlotActiveNodeIds);

    const nodeIds = [nodeId, ...missingIfMovedActiveNodeIds];
    const associations = nodeIds.flatMap(resolveNodeAssociations);
    const nodeIdsWithConfigReferences = nodeIds.flatMap(resolveNodeIdsWithConfigReferences);
    const blockingAssociations = intersection(
      missingIfMovedActiveNodeIds,
      associations.map((a) => a.nodeId),
    );
    const blockingConfigReferences = intersection(missingIfMovedActiveNodeIds, nodeIdsWithConfigReferences);
    return {
      associations: blockingAssociations,
      configReferences: blockingConfigReferences,
    };
  };

export const createCanMoveHereCreate = (getRuntimeManifest: GetRuntimeManifest) => (state: NodeDesignsState) => {
  const resolveLinkages = resolveNodeLinkagesPreventingMoveToSlotCreateFromState(state, getRuntimeManifest);
  return (nodeId: number, slotId: SlotId) => {
    const { configReferences, associations } = resolveLinkages(nodeId, slotId);
    return configReferences.length === 0 && associations.length === 0;
  };
};
